import { useTranslation } from 'react-i18next';
import { Grid, Paper, Stack, Typography } from '@mui/material';
import { RangePlanningItem } from '../types';
import CarbonRatingChip from '../../RecipeV2/components/CarbonRatingChip';
import DietaryAttributeChips from '../../RecipeV2/components/DietaryAttributeChips';

interface OverviewTabProps {
  item: RangePlanningItem;
}

const SummaryBlock = ({ label, value }: { label: string; value: string }) => (
  <Stack spacing={0.5}>
    <Typography variant="textXS"
      color="text.secondary">{label}</Typography>
    <Typography variant="textMD"
      fontWeight={600}>{value}</Typography>
  </Stack>
);

/** Read-only summary of the item, aggregating the headline fields from every other tab so
 * users don't need to click through all nine tabs just to get an at-a-glance view. */
const OverviewTab = ({ item }: OverviewTabProps) => {
  const { t } = useTranslation('rangePlanningItems');

  return (
    <Paper variant="outlined"
      sx={{ p: 4 }}>
      <Grid container
        spacing={5}>
        <Grid item
          md={3}
          sm={6}
          xs={12}>
          <SummaryBlock label={t('itemNumber', 'Item Number')}
            value={item.itemNumber} />
        </Grid>
        <Grid item
          md={3}
          sm={6}
          xs={12}>
          <SummaryBlock label={t('category', 'Category')}
            value={item.category} />
        </Grid>
        <Grid item
          md={3}
          sm={6}
          xs={12}>
          <SummaryBlock label={t('baseUom', 'Base UOM')}
            value={item.baseUom} />
        </Grid>
        <Grid item
          md={3}
          sm={6}
          xs={12}>
          <SummaryBlock label={t('supplier', 'Supplier')}
            value={item.supplier.supplierName} />
        </Grid>
        <Grid item
          md={3}
          sm={6}
          xs={12}>
          <SummaryBlock
            label={t('contractPrice', 'Contract Price')}
            value={`${item.pricing.currency} ${item.pricing.contractPrice.toFixed(2)}`}
          />
        </Grid>
        <Grid item
          md={3}
          sm={6}
          xs={12}>
          <Stack spacing={0.5}>
            <Typography variant="textXS"
              color="text.secondary">{t('carbon', 'Carbon')}</Typography>
            <CarbonRatingChip rating={item.carbonRating} />
          </Stack>
        </Grid>
        <Grid item
          xs={12}>
          <Stack spacing={0.5}>
            <Typography variant="textXS"
              color="text.secondary">{t('allergensAttributes', 'Allergens & Attributes')}</Typography>
            <DietaryAttributeChips attributes={item.dietaryAttributes} />
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default OverviewTab;
